import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import "./UserManagement.css";

const API_BASE = "/api";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  role: "therapist",
  password: "",
};

const ROLE_LABELS = {
  account_admin: "Account administrator",
  manager: "Manager",
  therapist: "Therapist",
  front_desk: "Front desk",
  read_only: "View only",
};

const STATUS_LABELS = {
  active: "Active",
  invited: "Invited",
  inactive: "Inactive",
};

function initialsFor(user) {
  const initials = `${user?.firstName?.[0] || ""}${
    user?.lastName?.[0] || ""
  }`.toUpperCase();

  return initials || user?.email?.[0]?.toUpperCase() || "?";
}

/*
 * accountId/accountName: set when 10 Digits staff manage another
 * client's users; requests then name that account.
 */
function UserManagement({
  token,
  currentUser,
  onClose,
  onCurrentUserUpdated,
  accountId = "",
  accountName = "",
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // { name, url, purpose, expiresAt }: a link to copy and send.
  const [shareLink, setShareLink] = useState(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(accountId ? { "X-Account-Id": accountId } : {}),
  };

  const authConfig = { headers: authHeaders };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API_BASE}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(accountId ? { "X-Account-Id": accountId } : {}),
        },
      });
      setUsers(response.data?.users || []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Unable to load users."
      );
    } finally {
      setLoading(false);
    }
  }, [token, accountId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const openCreateForm = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setError("");
    setNotice("");
    setShowForm(true);
  };

  const openEditForm = (user) => {
    setEditingUser(user);
    setForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "therapist",
      password: "",
    });
    setError("");
    setNotice("");
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
  };

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const saveUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      let savedUser;

      if (editingUser) {
        const response = await axios.patch(
          `${API_BASE}/users/${editingUser.id}`,
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            role: form.role,
          },
          authConfig
        );

        savedUser = response.data.user;

        if (form.password) {
          await axios.post(
            `${API_BASE}/users/${editingUser.id}/password`,
            { password: form.password },
            authConfig
          );
        }

        if (savedUser.id === currentUser?.id) {
          onCurrentUserUpdated(savedUser);
        }

        setNotice("User updated successfully.");
      } else {
        const response = await axios.post(
          `${API_BASE}/users`,
          {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            role: form.role,
            ...(form.password ? { password: form.password } : {}),
          },
          authConfig
        );

        savedUser = response.data.user;

        if (response.data.link) {
          setShareLink({
            name: `${savedUser.firstName} ${savedUser.lastName}`,
            purpose: "invite",
            ...response.data.link,
          });
          setNotice("User invited. Send them the link below.");
        } else {
          setNotice("User created successfully.");
        }
      }

      setShowForm(false);
      setEditingUser(null);
      setForm(EMPTY_FORM);
      await loadUsers();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Unable to save the user."
      );
    } finally {
      setSaving(false);
    }
  };

  const createLink = async (user) => {
    setError("");
    setNotice("");

    try {
      const response = await axios.post(
        `${API_BASE}/users/${user.id}/link`,
        {},
        authConfig
      );

      setShareLink({
        name: `${user.firstName} ${user.lastName}`,
        purpose: response.data.purpose,
        ...response.data.link,
      });
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Unable to create the link."
      );
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setNotice("Link copied.");
    } catch {
      setNotice("Select the link and copy it.");
    }
  };

  const changeStatus = async (user) => {
    const activate = user.status === "inactive";
    const action = activate
      ? "reactivate"
      : user.status === "invited"
        ? "cancel the invite for"
        : "deactivate";

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${user.firstName} ${user.lastName}?`
      )
    ) {
      return;
    }

    setError("");
    setNotice("");

    try {
      if (activate) {
        await axios.patch(
          `${API_BASE}/users/${user.id}`,
          { status: "active" },
          authConfig
        );
      } else {
        await axios.delete(`${API_BASE}/users/${user.id}`, authConfig);
      }

      setNotice(`User ${activate ? "reactivated" : "deactivated"}.`);
      setShareLink(null);
      await loadUsers();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          `Unable to ${action} the user.`
      );
    }
  };

  return (
    <div className="user-modal-backdrop" role="presentation">
      <section
        className="user-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-management-title"
      >
        <header className="user-modal-header">
          <div>
            <h2 id="user-management-title">Manage users</h2>
            <p>Add and maintain users for this account.</p>
          </div>
          <button
            type="button"
            className="user-modal-close"
            onClick={onClose}
            aria-label="Close user management"
          >
            ×
          </button>
        </header>

        <div className="user-modal-toolbar">
          <div>
            <strong>{accountName || currentUser?.account?.name || "Account"}</strong>
            <span>{users.length} users</span>
          </div>
          <button type="button" className="user-primary-button" onClick={openCreateForm}>
            + Add user
          </button>
        </div>

        {error && <div className="user-alert user-alert-error">{error}</div>}
        {notice && <div className="user-alert user-alert-success">{notice}</div>}

        {shareLink && (
          <div className="user-share-link">
            <strong>
              {shareLink.purpose === "invite" ? "Invite link" : "Password reset link"} for{" "}
              {shareLink.name}
            </strong>
            <span>
              Send this to them yourself. It works once and expires{" "}
              {new Date(shareLink.expiresAt).toLocaleString()}.
            </span>
            <div className="user-share-link-row">
              <input
                readOnly
                value={shareLink.url}
                onFocus={(event) => event.target.select()}
                aria-label="Link to send"
              />
              <button type="button" className="user-primary-button" onClick={copyLink}>
                Copy
              </button>
              <button type="button" className="user-secondary-button" onClick={() => setShareLink(null)}>
                Done
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <form className="user-form" onSubmit={saveUser}>
            <div className="user-form-heading">
              <strong>{editingUser ? "Edit user" : "Add user"}</strong>
              <span>
                {editingUser
                  ? "Leave password blank to keep the current password."
                  : "Leave the password blank to get an invite link to send instead."}
              </span>
            </div>

            <div className="user-form-grid">
              <label>
                First name
                <input
                  name="firstName"
                  value={form.firstName}
                  onChange={updateForm}
                  maxLength="100"
                  required
                />
              </label>
              <label>
                Last name
                <input
                  name="lastName"
                  value={form.lastName}
                  onChange={updateForm}
                  maxLength="100"
                  required
                />
              </label>
              <label className="user-form-wide">
                Email address
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={updateForm}
                  maxLength="254"
                  required
                />
              </label>
              <label>
                Role
                <select name="role" value={form.role} onChange={updateForm}>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {editingUser ? "New password" : "Temporary password (optional)"}
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={updateForm}
                  minLength="12"
                  maxLength="128"
                  autoComplete="new-password"
                />
              </label>
            </div>

            <div className="user-form-actions">
              <button type="button" className="user-secondary-button" onClick={closeForm}>
                Cancel
              </button>
              <button type="submit" className="user-primary-button" disabled={saving}>
                {saving ? "Saving..." : editingUser ? "Save changes" : "Create user"}
              </button>
            </div>
          </form>
        )}

        <div className="user-list">
          {loading ? (
            <div className="user-list-message">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="user-list-message">No users found.</div>
          ) : (
            users.map((user) => {
              const isCurrentUser = user.id === currentUser?.id;
              const inactive = user.status === "inactive";

              return (
                <article className={`user-row ${inactive ? "user-row-inactive" : ""}`} key={user.id}>
                  <div className="user-list-avatar">{initialsFor(user)}</div>
                  <div className="user-list-identity">
                    <strong>
                      {user.firstName} {user.lastName}
                      {isCurrentUser && <span className="user-you-label">You</span>}
                    </strong>
                    <span>{user.email}</span>
                  </div>
                  <div className="user-list-details">
                    <span>{ROLE_LABELS[user.role] || user.role}</span>
                    <span className={`user-status user-status-${user.status}`}>
                      {STATUS_LABELS[user.status] || user.status}
                    </span>
                  </div>
                  <div className="user-row-actions">
                    <button type="button" className="user-secondary-button" onClick={() => openEditForm(user)}>
                      Edit
                    </button>
                    {!inactive && !isCurrentUser && (
                      <button type="button" className="user-secondary-button" onClick={() => createLink(user)}>
                        {user.status === "invited" ? "New invite link" : "Reset link"}
                      </button>
                    )}
                    <button
                      type="button"
                      className={inactive ? "user-secondary-button" : "user-danger-button"}
                      onClick={() => changeStatus(user)}
                      disabled={isCurrentUser && !inactive}
                      title={isCurrentUser && !inactive ? "You cannot deactivate yourself" : ""}
                    >
                      {inactive ? "Reactivate" : user.status === "invited" ? "Cancel invite" : "Deactivate"}
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}

export default UserManagement;
